import type { OzonChange, OzonChangePlan } from "@/api/ozonSupply";

/** Режим изменений состава как ЧИСТАЯ модель: что устарело, что покажет превью и где стоит каскад.
 *
 *  ⚠️ Вся логика режима живёт здесь, а не в разметке. Экран показывает необратимое (коды мест
 *  снимаются, этикетки становятся мусором), и решение «можно ли применять» обязано быть одним,
 *  проверяемым тестом, а не суммой условий в трёх компонентах.
 */

/** Шаги каскада в том порядке, в котором их делает бэкенд (`ozon_change.CASCADE_STEPS`).
 *
 *  ⚠️ Порядок задан НЕОБРАТИМОСТЬЮ: состав заявки — единственный отказуемый без последствий,
 *  дальше удаляются коды, и назад дороги нет. Расходиться с бэкендом этому списку нельзя —
 *  неизвестный шаг мы не рисуем «ожидает», а роняем громко (`stepsOf`). */
export const CASCADE_STEPS = [
  "content",
  "cargo_delete",
  "cargo_send",
  "onec_unpost",
  "onec_rows",
  "onec_repost",
  "files",
  // последним: этикетки новых коробов уже в заказе 1С, что бы ни ответил портал склада
  "pallet_plan",
] as const;

export type CascadeStep = (typeof CASCADE_STEPS)[number];

/** Что именно делает шаг — человеческими словами. Служебное имя (`onec_unpost`) на экране
 *  прогресса не говорит менеджеру ничего, а ждать его приходится минутами. */
export const STEP_LABEL: Record<CascadeStep, string> = {
  content: "Состав заявки на площадке",
  cargo_delete: "Снятие кодов у опустевших и изменённых мест",
  cargo_send: "Досылка новых мест и коробов — площадка выдаёт коды",
  onec_unpost: "Распроведение документов 1С",
  onec_rows: "Правка строк и паллет 1С",
  onec_repost: "Проведение документов 1С обратно",
  files: "Бумаги новой версии в карточке заказа 1С",
  // только при PALLETS_TARGET=portal: паллеты — ревизией плана в портал склада, а не в 1С
  pallet_plan: "План паллет в портале склада",
};

export const STATE_EDITING = "editing";
export const STATE_DONE = "done";
export const STATE_ABORTED = "aborted";

/** Идёт ли каскад прямо сейчас: шаг наружу и БЕЗ записанной ошибки.
 *
 *  ⚠️ Ровно то же правило, по которому бэкенд отсекает второй параллельный `apply`: сорвавшийся
 *  шаг причину пишет всегда, поэтому шаг без ошибки читается как «кто-то идёт прямо сейчас». По
 *  нему экран и опрашивает `GET change` раз в две секунды. */
export function applying(ch: OzonChange): boolean {
  return (
    ch.error === null && (CASCADE_STEPS as readonly string[]).includes(ch.state)
  );
}

/** Правка ещё ПРАВИТСЯ: раскладку можно менять, заход — отменить.
 *
 *  ⚠️ `editing` с ошибкой — это НАЗВАННЫЙ отказ площадки на шаге состава (единственный откат
 *  назад в каскаде): править и отменять по-прежнему можно, но причину человек обязан прочитать. */
export function editing(ch: OzonChange): boolean {
  return ch.state === STATE_EDITING;
}

/** Каскад сорвался и ждёт продолжения: стоит на шаге и знает, чем сорвался. */
export function failed(ch: OzonChange): boolean {
  return (
    ch.error !== null && (CASCADE_STEPS as readonly string[]).includes(ch.state)
  );
}

/* ────────────────────────── что устарело до применения ────────────────────────── */

/** Что разошлось с правдой, пока правка не применена. Всё, что здесь `true`, на бумаге и у
 *  площадки СТАРОЕ — печатать и записывать в 1С нельзя. */
export interface Stale {
  /** состав заявки или набор грузомест у площадки */
  ozon: boolean;
  /** строки и паллеты в документах 1С */
  onec: boolean;
  /** этикетки коробов и наклейки палет */
  labels: boolean;
}

/** Что устареет, если применить план. План не посчитан (`null`) — считаем устаревшим ВСЁ.
 *
 *  ⚠️ Именно так, а не «ничего»: пока превью не сошлось, экран не знает, что изменилось, и
 *  «всё в порядке» было бы обещанием, которого он дать не может. Печать в это время всё равно
 *  заблокирована — правка идёт.
 */
export function staleOf(plan: OzonChangePlan | null): Stale {
  if (plan === null) return { ozon: true, onec: true, labels: true };
  const p = plan.places;
  return {
    ozon:
      plan.content.length > 0 ||
      p.emptied.length > 0 ||
      p.changed.length > 0 ||
      p.new.length > 0,
    onec:
      plan.onec !== null &&
      (sum(plan.onec.rows) > 0 || sum(plan.onec.pallets) > 0),
    // ⚠️ Этикетки УСТАРЕТЬ могут только у того, кто их печатал: правка до выдачи кодов заводит
    // полную поставку новых мест, но ни одной наклейки по ней ещё нет — и «этикетки устарели»
    // отправляло бы искать их на коробах
    labels:
      !plan.labels.first_print &&
      (plan.labels.boxes.length > 0 || plan.labels.pallets),
  };
}

const sum = (d: Record<string, number>): number =>
  Object.values(d).reduce((s, n) => s + n, 0);

/** Сколько правок накопил заход — ПО ПЛАНУ, а не по числу нажатий.
 *
 *  ⚠️ Считать нажатия бессмысленно: три переноса туда-обратно — ноль правок, а одна замена партии
 *  не меняет состав заявки вовсе (меняются места). Поэтому две цифры: строки состава и места. */
export interface ChangeSize {
  /** строк состава заявки изменилось (в том числе добавленных и убранных) */
  content: number;
  /** мест затронуто: опустевшие под снятие кодов, изменённые по коробам и новые под досылку */
  places: number;
  total: number;
}

export function changeSize(plan: OzonChangePlan | null): ChangeSize | null {
  if (plan === null) return null;
  const places =
    plan.places.emptied.length +
    plan.places.changed.length +
    plan.places.new.length;
  return {
    content: plan.content.length,
    places,
    total: plan.content.length + places,
  };
}

/* ────────────────────────── модель превью ────────────────────────── */

/** Почему «Применить изменения» сейчас нажимать нельзя; `null` — можно.
 *
 *  ⚠️ Кнопка не исчезает, а гаснет с причиной: пропади она — экран не сказал бы, чего не хватает.
 *  Причин ровно три, и каждая своя: план не считан, план запрещает, применять нечего.
 */
export function applyBlock(
  plan: OzonChangePlan | null,
  /** текст ошибки запроса превью; `null` — запрос прошёл */
  error: string | null = null,
): string | null {
  if (error) return "превью не посчиталось";
  if (plan === null) return "превью ещё не посчитано";
  if (plan.mode === "blocked") return "план не сходится — см. проблемы ниже";
  if (plan.mode === "none") return "изменений нет";
  return null;
}

/** Строка состава превью: было → станет, и что это значит словами. */
export interface ContentLine {
  mp_sku: string;
  article: string;
  agreed: number;
  target: number;
  /** added — площадка о товаре не знает · removed — не повезём вовсе · changed — другое число */
  kind: "added" | "removed" | "changed";
}

export function contentLines(plan: OzonChangePlan): ContentLine[] {
  return plan.content.map((r) => ({
    ...r,
    kind: r.agreed === 0 ? "added" : r.target === 0 ? "removed" : "changed",
  }));
}

/** Крупное предупреждение режима — то, что человек обязан прочитать ДО нажатия; `null` — нет. */
export function modeWarning(plan: OzonChangePlan): string | null {
  if (plan.mode !== "replace") return null;
  // ⚠️ Порог — по КОРОБАМ, а не по местам: пока жив хоть один нетронутый код, правка идёт дельтой.
  // Замена нужна только там, где снимаются ВСЕ коды: площадка не даёт удалить все грузоместа
  // разом (`CANT_DELETE_ALL_CARGOES`)
  return (
    "Нетронутых коробов с кодом не осталось ни одного: площадка не даёт удалить все грузоместа " +
    "разом, и набор уедет ЗАМЕНОЙ ЦЕЛИКОМ. Коды сменятся у ВСЕХ коробов, а все напечатанные " +
    "этикетки станут недействительными — перепечатывать придётся всю стопку."
  );
}

/* ────────────────────────── что разрешает сам режим ────────────────────────── */

/** Можно ли отметить ОДНУ строку товара внутри раскрытого места.
 *
 *  ⚠️ Вне режима — только у места без кода: строку уехавшего места не переложить, а само место
 *  отметить надо (иначе с него не снять коды). В режиме запрет снят: инвариант держится на КОРОБЕ,
 *  а не на месте (`ozon_places`, 18.09.2026). Убрать строку или уменьшить её количество у места с
 *  кодами можно НА МЕСТЕ — снимутся коды ровно затронутых коробов, а остаток уедет досылкой в то
 *  же место; целиком по-прежнему выносится только ПЕРЕНОС в другое место (`_out_of_frozen`).
 *  Без выбора строки всё это было бы недостижимо: снять с места один артикул из нескольких нечем.
 *
 *  Своих проверок «а можно ли именно это» экран не заводит: вынос части, возврат в место с кодом
 *  и смену типа отвергает бэкенд, и отказ он называет словами. */
export function canPickItem(
  cargoId: string | null,
  changeMode: boolean,
): boolean {
  return changeMode || cargoId === null;
}

/** Почему «Этикетки затронутых» сейчас не печатаются; `null` — печатаются.
 *
 *  ⚠️ Напоминание о наклейках (`direction.reprint`) считается по ПОСЛЕДНЕМУ ЗАВЕРШЁННОМУ заходу,
 *  а ручка печати (`changed_only=1`) берёт НОВЕЙШИЙ: при открытом новом заходе она ответила бы
 *  409. Само напоминание при этом остаётся — короба без наклеек никуда не делись, — а кнопка
 *  гаснет с причиной, ровно как остальная печать в режиме изменений. */
export function reprintBlock(change: OzonChange | null): string | null {
  return change === null ? null : "печать после применения текущих изменений";
}

/* ────────────────────────── прогресс применения ────────────────────────── */

export type StepStatus = "done" | "running" | "failed" | "waiting";

export interface StepRow {
  key: CascadeStep;
  label: string;
  status: StepStatus;
  /** чем сорвался ЭТОТ шаг; пусто — не срывался */
  error: string | null;
}

/** Где стоит каскад: шаг за шагом, с одним «идёт» или одним «сбой».
 *
 *  ⚠️ Неизвестное состояние — исключение, а не «всё ожидает»: нарисованный ровным списком
 *  испорченный `state` означал бы экран, который бодро показывает прогресс поставки, где на
 *  самом деле неизвестно что. Молчать об этом нельзя.
 */
/** `palletsToPortal` — паллеты пишутся в портал склада (`onec.pallets_target === "portal"`): только
 *  тогда у каскада есть шаг `pallet_plan`. В режиме `1c` его нет, и показанный «сделано» он
 *  врал бы; стоит каскад на нём (режим переключили посреди) — показываем, он настоящий. */
export function stepsOf(ch: OzonChange, palletsToPortal: boolean): StepRow[] {
  const known: string[] = [
    STATE_EDITING,
    STATE_DONE,
    STATE_ABORTED,
    ...CASCADE_STEPS,
  ];
  if (!known.includes(ch.state))
    throw new Error(
      `Изменение №${ch.id}: неизвестный шаг «${ch.state}» — портал не знает, что происходит`,
    );
  const steps = CASCADE_STEPS.filter(
    (key) => key !== "pallet_plan" || palletsToPortal || ch.state === key,
  );
  const at = (steps as readonly string[]).indexOf(ch.state);
  return steps.map((key, i) => ({
    key,
    label: STEP_LABEL[key],
    error: at === i ? ch.error : null,
    status: status(ch, at, i),
  }));
}

function status(ch: OzonChange, at: number, i: number): StepStatus {
  if (ch.state === STATE_DONE) return "done";
  // до первого шага и после отката каскад не начинался: показывать «сделано» не за что
  if (ch.state === STATE_EDITING || ch.state === STATE_ABORTED)
    return "waiting";
  if (i < at) return "done";
  if (i > at) return "waiting";
  return ch.error === null ? "running" : "failed";
}
